import { Code2, File, FileText, Globe, Image, LayoutGrid, Presentation } from "lucide-react";
import { useId } from "react";
import type { LibraryPin } from "../lib/library-preferences";
import { libraryIconColors, libraryTone } from "../lib/library-tone";

const symbols = {
  site: Globe,
  app: LayoutGrid,
  code: Code2,
  page: FileText,
  slides: Presentation,
  image: Image,
  file: File,
};

/** A local, deterministic sculpted icon: no file contents or generation credentials needed. */
export function LibraryPinIcon({ item }: { item: LibraryPin }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const tone = libraryTone(item);
  const Symbol = symbols[tone];
  const seed = Array.from(item.id).reduce((n, c) => (n * 31 + c.charCodeAt(0)) >>> 0, 0);
  const letters = (item.name.match(/[\p{L}\p{N}]+/gu) ?? [])
    .slice(0, 2)
    .map((word) => Array.from(word)[0])
    .join("")
    .toLocaleUpperCase();
  return (
    <svg
      viewBox="0 0 64 64"
      width={48}
      height={48}
      aria-hidden="true"
      data-testid="library-pin-icon"
      data-icon-kind={tone}
      className={`size-12 shrink-0 overflow-visible ${libraryIconColors[tone]}`}
    >
      <defs>
        <linearGradient id={`${id}-face`} x1="0" y1="0" x2="0.8" y2="1">
          <stop stopColor="currentColor" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.82" />
        </linearGradient>
        <linearGradient id={`${id}-shine`} x1="0" y1="0" x2="0.7" y2="1">
          <stop stopColor="var(--chat-user-foreground)" stopOpacity="0.75" />
          <stop offset="0.48" stopColor="var(--chat-user-foreground)" stopOpacity="0.04" />
          <stop offset="1" stopColor="var(--overlay)" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <ellipse cx="33" cy="59" rx="21" ry="2.5" fill="currentColor" opacity="0.14" />
      <g transform={`rotate(${(seed % 9) - 4} 32 31)`}>
        <rect x="12" y="13" width="44" height="43" rx="13" fill="currentColor" />
        <rect x="12" y="13" width="44" height="43" rx="13" fill="var(--overlay)" opacity="0.28" />
        <rect x="7" y="6" width="44" height="44" rx="13" fill={`url(#${id}-face)`} />
        <rect x="7" y="6" width="44" height="44" rx="13" fill={`url(#${id}-shine)`} />
        <rect
          x="9"
          y="8"
          width="40"
          height="40"
          rx="11"
          fill="none"
          stroke="var(--chat-user-foreground)"
          strokeOpacity="0.28"
        />
        <Symbol
          x={16}
          y={16}
          width={26}
          height={26}
          strokeWidth={3.1}
          stroke="var(--overlay)"
          opacity={0.28}
        />
        <Symbol
          x={15}
          y={14}
          width={26}
          height={26}
          strokeWidth={2.8}
          stroke="var(--chat-user-foreground)"
        />
        <rect x="35" y="37" width="23" height="16" rx="6" fill="var(--chat-user-foreground)" />
        <text
          x="46.5"
          y="48.3"
          textAnchor="middle"
          fill="currentColor"
          fontSize="9"
          fontWeight="800"
          fontFamily="inherit"
        >
          {letters || "F"}
        </text>
      </g>
    </svg>
  );
}
